/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import * as constants from '../../common/constants';
import env from '../../common/env';
import Framework from '../../framework/Framework';
import Utils from '../../utils/Utils';

/**
 * Authentication Strategy for authenticating the user against Pearson Identity (PI/IES) using piSession.js.
 *
 * @author Hari Gangadharan
 */
export default class IesAuthStrategy {
  constructor(authService) {
    this.isInitialized = false;
    this.authService = authService;
  }

  login(successUrl) {
    const piSession = window.piSession;
    piSession.login(successUrl, 100, (status, accessToken) => {
      if (status === piSession.Success) {
        const userId = piSession.userId();
        const contextId = piSession.getContextId();
        this.authService.setAuthenticated(userId, accessToken, contextId);
        setTimeout(() => { this.login(window.location.href); }, constants.LOGIN_REFRESH_TIME);
        piSession.on(piSession.SessionTimedOutEvent, this.logout);
      } else {
        // something wrong
        const logoutRedirectionUrl = env.get('LOGOUT_REDIRECTION_URL');
        if (logoutRedirectionUrl) {
          // DL App should redirect to select role page
          piSession.setOptions({
            requireLogin: false
          });
          piSession.logout(logoutRedirectionUrl);
        } else {
          piSession.logout();
        }
      }
    });
  }

  logout = () => {
    const piSession = window.piSession;
    const isCanadianProxy = Utils.getQueryParameterbyName('cp');
    let redirectURL = '';
    const user = Framework.getStoreRegistry().getStore('user');
    if (sessionStorage.getItem('sortByActive')) {
      sessionStorage.removeItem('sortByActive');
      sessionStorage.removeItem('activeValue');
    }
    if (sessionStorage.getItem('sortByInactive')) {
      sessionStorage.removeItem('sortByInactive');
      sessionStorage.removeItem('inactiveValue');
    }
    if (isCanadianProxy === '1') {
      redirectURL = `${env.get('CP_URL')}`;
    }

    if (user.isMojoUser) {
      console.log('Log from on Logout', user.isMojoUser); // TODO: Remove once it is tested
      redirectURL = `${env.get('PMC_SIGNIN_URL')}`;
    }
    const logoutRedirectionUrl = `${env.get('LOGOUT_REDIRECTION_URL')}`;
    if (logoutRedirectionUrl) {
      redirectURL = logoutRedirectionUrl;
    }
    if (redirectURL && piSession) {
      piSession.setOptions({
        requireLogin: false
      });
      piSession.logout(redirectURL);
    }
    if (redirectURL && !piSession) {
      window.location.href = redirectURL;
    }
  };

  /**
   * Load piSession.js script.
   */
  init(onInit) {
    if (!this.isInitialized) {
      const sessionTime = this.authService.getSessionDuration();
      this.clientId = this.authService.getClientId();
      this.isInitialized = true;
      const script = document.createElement('script');
      script.onload = () => {
        const piSession = window.piSession;
        if (piSession) {
          piSession.initialize(this.clientId, {
            monitorUserActivity: true,
            sessionIdleTimeoutWarningSeconds: 300,
            useDefaultIdleTimoutWarningPopup: false,
            requireLogin: true,
            useJwt: true,
            checkSessionMilliseconds: 3000,
            sessionIdleTimeoutSeconds: sessionTime
          });
          this.login(window.location.href);
        }
        onInit();
      };
      script.src = `${env.get('IES_BASE_URL')}/login/js/v2/session.js`;
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  }

  /**
   * To get live token from piSession
   * @returns
   */
  getToken = () => new Promise((resolve) => {
    window.piSession.getToken((status, accessToken) => {
      resolve(accessToken);
    });
  });
}
